<script lang="ts">
export default {
    name: 'ProductListWrapper',
};
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { watchDebounced } from '@vueuse/core';
import { isEmpty, orderBy } from 'lodash-es';
import { useRoute } from 'vue-router/composables';
import ProductListPanel from '@/components/app/product/list/panel/index.vue';

// @ts-expect-error not typed yet
import usePageData from '@/composables/usePageData.js';

type ProductListItem = Record<string, unknown>;
type ProductListResponse = {
    list?: ProductListItem[];
};

const props = defineProps<{
    list?: ProductListItem[];
    title?: string;
    titleMessage?: string;
    createButton?: string;
    createRoute?: string;
    connectButton?: string;
    connectTitle?: string;
    fetcher?: () => Promise<ProductListResponse>;
    externalList?: boolean;
    noSearch?: boolean;
    noWorkspace?: boolean;
}>();

const internal = ref<ProductListItem[]>([]);

const displayList = computed(() => props.externalList ? (props.list ?? []) : internal.value);

const route = useRoute();

const hasResults = computed(() => !isEmpty(displayList.value));
const hasActiveFilters = computed(() => ['search', 'workspace'].some((name) => {
    const value = route.query[name];

    return Array.isArray(value) ? value.some(Boolean) : Boolean(value);
}));
const refetchPending = ref(false);
const showContent = computed(() => hasResults.value || hasActiveFilters.value || refetchPending.value);

const { fetchData, loader, loaded } = usePageData({
    fetcher: props.fetcher,
    onFetchData(data: ProductListResponse) {
        if (props.externalList) return;

        internal.value = orderBy(data.list ?? [], 'updatedAt', 'desc');
    },
});

const showFilteredEmpty = computed(() => loaded.value && !refetchPending.value && !hasResults.value);

let queryVersion = 0;

watch(
    () => route.query,
    () => {
        queryVersion += 1;
        refetchPending.value = true;
    },
);

watchDebounced(
    () => route.query,
    async () => {
        const version = queryVersion;

        await fetchData();

        if (version === queryVersion) {
            refetchPending.value = false;
        }
    },
    { debounce: 300 },
);
</script>

<template>
    <div class="flex h-full w-full flex-col items-center" v-loader="!loaded && loader">
        <div class="w-full max-w-360 flex-1 pt-6 pb-8 lg:pt-8 2xl:pt-12">
            <div v-if="loaded && showContent" class="flex h-full flex-col space-y-2 sm:space-y-6">
                <div class="flex max-w-360 flex-col justify-between space-y-2 px-4 sm:px-6 lg:flex-row lg:space-y-0 lg:gap-x-4 lg:px-8">
                    <slot name="title">
                        <div class="flex flex-col space-y-2">
                            <span v-text="title" class="text-2xl font-semibold lg:text-3xl" />

                            <span v-html="titleMessage" class="text-base text-gray-600 lg:text-lg" />
                        </div>
                    </slot>

                    <slot name="actions">
                        <base-button
                            v-if="createRoute"
                            class="self-start sm:block"
                            type="primary"
                            round
                            @click="$router.push({ name: createRoute })"
                        >
                            {{ createButton }}
                        </base-button>
                    </slot>
                </div>

                <div
                    v-if="!noSearch || !noWorkspace"
                    class="flex sm:mx-6 lg:mx-8"
                >
                    <product-list-panel
                        :no-search="noSearch"
                        :no-workspace="noWorkspace"
                    />
                </div>

                <div v-loader="loaded && loader" class="sm:mx-6 lg:mx-8">
                    <slot v-if="hasResults" name="list" :list="displayList" />
                    <slot v-else-if="showFilteredEmpty" name="filtered-empty">
                        <div class="flex w-full items-center justify-center rounded-md bg-gray-100 px-6 py-12 text-gray-500">
                            No results found for the selected filters.
                        </div>
                    </slot>
                </div>
            </div>
            <div v-else-if="loaded" class="flex h-full w-full flex-col items-center justify-center">
                <div class="mb-12 flex w-full max-w-3xl flex-col items-center justify-center gap-y-4">
                    <slot name="empty">
                        <base-button
                            type="primary"
                            round
                            @click="$router.push({ name: createRoute })"
                        >
                            <span class="px-4 text-xl leading-10">
                                {{ connectButton }}
                            </span>
                        </base-button>
                        <span class="text-center text-xl">
                            {{ connectTitle }}
                        </span>
                        <div class="flex items-center justify-center gap-x-2 text-xs text-gray-500">
                            <base-icon name="lock" prefix="fas" />
                            <span>
                                {{ $t('no credit card') }}
                            </span>
                        </div>
                    </slot>
                </div>
            </div>
        </div>
    </div>
</template>
